import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const PinModal = ({ isOpen, onClose, onSuccess, mode = 'verify' }) => {
  // mode: 'verify' | 'setup' | 'change'
  const [pin, setPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // Para setup/change: 1=current, 2=new, 3=confirm
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setCurrentPin('');
      setConfirmPin('');
      setError('');
      setStep(1);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleVerify = async () => {
    if (pin.length < 4) {
      setError('El PIN debe tener al menos 4 digitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/users/me/verify-pin', { pin });
      if (response.data.valid) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'PIN incorrecto');
      setPin('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    const isChangeMode = mode === 'change';
    const confirmStep = isChangeMode ? 3 : 2;
    const newPinStep = isChangeMode ? 2 : 1;

    // Step 1 en change mode: Verificar PIN actual
    if (isChangeMode && step === 1) {
      if (currentPin.length < 4) {
        setError('Ingresa tu PIN actual');
        return;
      }
      setLoading(true);
      try {
        const response = await api.post('/api/users/me/verify-pin', { pin: currentPin });
        if (response.data.valid) {
          setStep(2);
          setError('');
        }
      } catch (err) {
        setError('PIN actual incorrecto');
        setCurrentPin('');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Step de ingresar nuevo PIN
    if (step === newPinStep) {
      if (pin.length < 4) {
        setError('El PIN debe tener al menos 4 digitos');
        return;
      }
      if (pin.length > 6) {
        setError('El PIN debe tener maximo 6 digitos');
        return;
      }
      setStep(confirmStep);
      setError('');
      return;
    }

    // Step final: confirmar y guardar
    if (step === confirmStep) {
      if (confirmPin !== pin) {
        setError('Los PIN no coinciden');
        setConfirmPin('');
        return;
      }

      setLoading(true);
      setError('');

      try {
        await api.put('/api/users/me/pin', {
          currentPin: isChangeMode ? currentPin : null,
          newPin: pin
        });
        onSuccess();
      } catch (err) {
        setError(err.response?.data?.message || 'Error al guardar PIN');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemovePin = async () => {
    if (currentPin.length < 4) {
      setError('Ingresa tu PIN actual para eliminarlo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.put('/api/users/me/pin', {
        currentPin: currentPin,
        newPin: null
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar PIN');
      setCurrentPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (mode === 'verify') {
        handleVerify();
      } else {
        handleSetup();
      }
    }
  };

  const handleInputChange = (e, setter) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setter(value);
  };

  if (!isOpen) return null;

  const renderVerifyMode = () => (
    <>
      <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
        Ingresa tu PIN
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
        Ingresa tu PIN de 4-6 digitos para acceder a las preferencias
      </p>
      <input
        ref={inputRef}
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        value={pin}
        onChange={(e) => handleInputChange(e, setPin)}
        onKeyDown={handleKeyDown}
        placeholder="****"
        maxLength={6}
        style={{
          width: '100%',
          padding: '1rem',
          fontSize: '1.5rem',
          textAlign: 'center',
          letterSpacing: '0.5rem',
          border: '2px solid var(--border-color)',
          borderRadius: '8px',
          background: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          marginBottom: '1rem'
        }}
      />
      {error && (
        <p style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
        <button
          onClick={handleVerify}
          disabled={loading || pin.length < 4}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: loading || pin.length < 4 ? '#6b7280' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading || pin.length < 4 ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Verificando...' : 'Confirmar'}
        </button>
      </div>
    </>
  );

  const renderSetupMode = () => {
    const isChangeMode = mode === 'change';

    return (
      <>
        <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
          {isChangeMode ? 'Cambiar PIN' : 'Configurar PIN'}
        </h2>

        {/* Step indicators */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {isChangeMode && (
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: step >= 1 ? '#3b82f6' : '#4b5563'
            }} />
          )}
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: step >= (isChangeMode ? 2 : 1) ? '#3b82f6' : '#4b5563'
          }} />
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: step >= (isChangeMode ? 3 : 2) ? '#3b82f6' : '#4b5563'
          }} />
        </div>

        {/* Current PIN (solo para change) */}
        {isChangeMode && step === 1 && (
          <>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
              Ingresa tu PIN actual
            </p>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={currentPin}
              onChange={(e) => handleInputChange(e, setCurrentPin)}
              onKeyDown={handleKeyDown}
              placeholder="****"
              maxLength={6}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '2px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                marginBottom: '1rem'
              }}
            />
          </>
        )}

        {/* New PIN */}
        {((!isChangeMode && step === 1) || (isChangeMode && step === 2)) && (
          <>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
              {isChangeMode ? 'Ingresa tu nuevo PIN (4-6 digitos)' : 'Crea un PIN de 4-6 digitos'}
            </p>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => handleInputChange(e, setPin)}
              onKeyDown={handleKeyDown}
              placeholder="****"
              maxLength={6}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '2px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                marginBottom: '1rem'
              }}
            />
          </>
        )}

        {/* Confirm PIN */}
        {((!isChangeMode && step === 2) || (isChangeMode && step === 3)) && (
          <>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
              Confirma tu PIN
            </p>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={confirmPin}
              onChange={(e) => handleInputChange(e, setConfirmPin)}
              onKeyDown={handleKeyDown}
              placeholder="****"
              maxLength={6}
              autoFocus
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '2px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                marginBottom: '1rem'
              }}
            />
          </>
        )}

        {error && (
          <p style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '0.875rem' }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSetup}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: loading ? '#6b7280' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Guardando...' : (step === (isChangeMode ? 3 : 2) ? 'Guardar' : 'Siguiente')}
          </button>
        </div>

        {/* Opcion para eliminar PIN */}
        {isChangeMode && step === 1 && (
          <button
            onClick={handleRemovePin}
            disabled={loading || currentPin.length < 4}
            style={{
              width: '100%',
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'transparent',
              color: '#ef4444',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              cursor: loading || currentPin.length < 4 ? 'not-allowed' : 'pointer',
              opacity: loading || currentPin.length < 4 ? 0.5 : 1
            }}
          >
            Eliminar PIN
          </button>
        )}
      </>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'var(--bg-primary)',
        borderRadius: '12px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '320px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        {mode === 'verify' ? renderVerifyMode() : renderSetupMode()}
      </div>
    </div>
  );
};

export default PinModal;
